// Fetch epoch summaries by scanning sui_getCheckpoints (LOCAL / CPC fullnode).

import { useSuiClient } from '@mysten/dapp-kit';
import type { Checkpoint } from '@mysten/sui.js/client';
import { useQuery } from '@tanstack/react-query';

import {
	summarizeEpochsFromCheckpoints,
	type CpcEpochSummary,
} from '~/utils/cpcEpochFromCheckpoints';

const CHECKPOINT_PAGE_SIZE = 100;
const MAX_PAGES = 80;

async function fetchCheckpointsForEpochs(
	client: ReturnType<typeof useSuiClient>,
	targetEpochs: number,
): Promise<{ epochs: CpcEpochSummary[]; currentEpoch: number | null }> {
	const collected: Checkpoint[] = [];
	let cursor: string | null | undefined = null;
	let pages = 0;
	let distinctEpochs = new Set<number>();

	while (pages < MAX_PAGES) {
		const page = await client.getCheckpoints({
			descendingOrder: true,
			cursor,
			limit: CHECKPOINT_PAGE_SIZE,
		});
		if (!page.data.length) break;

		for (const cp of page.data) {
			collected.push(cp);
			distinctEpochs.add(Number(cp.epoch));
		}

		pages += 1;
		if (distinctEpochs.size > targetEpochs) break;
		if (!page.hasNextPage || !page.nextCursor) break;
		cursor = page.nextCursor;
	}

	const epochs = summarizeEpochsFromCheckpoints(collected, targetEpochs);
	const latest = collected[0];
	const currentEpoch = latest ? Number(latest.epoch) + 1 : null;

	return { epochs, currentEpoch };
}

export function useCpcEpochSummaries(limit = 20, refetchInterval?: number) {
	const client = useSuiClient();
	return useQuery({
		queryKey: ['cpc-epoch-summaries', limit],
		queryFn: () => fetchCheckpointsForEpochs(client, limit),
		staleTime: 30_000,
		refetchInterval,
		retry: 2,
	});
}
